import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button, 
  Card, 
  CardActions, 
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  FormHelperText,
  Grid,
  Box,
  IconButton,
  MenuItem,
  Chip,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Company } from '../../../types/UserTypes';
import axios from '../../../../services/ApiService';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { StaticData, CompanyCategory } from '../../../types/DataTypes';
import { paths } from '../../../../utils/paths';
import { Edit } from '@mui/icons-material';

function CompanyServiceInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = (location.state as { companyId?: number })?.companyId;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [categories, setCategories] = useState<CompanyCategory[]>([]);
  const [regions, setRegions] = useState<StaticData[]>([]);
  const [sectors, setSectors] = useState<StaticData[]>([]);
  const [companyData, setCompanyData] = useState<Company>({
    companyId: 0,
    userId: 0,
    agentId: 0,
    firstName: '',
    lastName: '',
    companyName: '',
    agentABN: '',
    email: '',
    contact: '',
    isDefault: false,
    isVerified: false,
    verificationComment: '',
    address: '',
    status: BVTConstants.ACTIVE,
    certId: 0,
    notes: '',
    webLink: '',
    logoId: 0,
    serviceId: 0,
    categoryId: 0,
    regions: [],
    sectors: [],
    bio: '',
    platformFee: false,
    feeAmount: 0,
    frequency: 'MONTHLY',
    createdAt: '',
    updatedAt: '',
    lastLoginAt: '',
  });

  useEffect(() => {
    fetchCompanyData();
    fetchCategories();
    fetchRegions();
    fetchSectors();
  }, []);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      if(companyId){
        const response = await axios.post("/getCompany", companyId);
        setCompanyData(response.data);
      } else {
        const response = await axios.get("/getCurrentUserCompany");
        setCompanyData(response.data);
      }
    } catch (error) {
      console.error('Error fetching company data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategories([]);
    setLoading(true);
    try {
      const response = await axios.get('/getCompanyCategories');
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    setRegions([]);
    setLoading(true);
    try {
      const response = await axios.post('/getStaticData', BVTConstants.COMPANY_REGION);
      if (Array.isArray(response.data)) {
        setRegions(response.data);
      }
    } catch (error) {
      console.error('Error fetching regions.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    setSectors([]);
    setLoading(true);
    try {
      const response = await axios.post('/getStaticData', BVTConstants.COMPANY_SECTOR);
      if (Array.isArray(response.data)) {
        setSectors(response.data);
      }
    } catch (error) {
      console.error('Error fetching sectors.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find(cat => cat.catId === categoryId);
    return category ? category.catName : 'N/A';
  };

  const getRegionNames = (regionIds: number[]) => {
    if (!regionIds || regionIds.length === 0) {
      return ['N/A'];
    }
    return regionIds.map(id => {
      const region = regions.find(reg => reg.dataId === id);
      return region ? region.dataValue : 'N/A';
    });
  };

  const getSectorNames = (sectorIds: number[]) => {
    if (!sectorIds || sectorIds.length === 0) {
      return ['N/A'];
    }
    return sectorIds.map(id => {
      const sector = sectors.find(sec => sec.dataId === id);
      return sector ? sector.dataValue : 'N/A';
    });
  };

  return (
    <>
      <Stack spacing={3}>
        <Card>
          <CardHeader 
            title="Service Profile"
            action={
              <>
                <IconButton 
                  aria-label="close" 
                  onClick={() => navigate(paths.agent.companyEditServiceInfo, { state: { companyId: companyData?.companyId } })}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    ml: 1
                  }}
                >
                  <Edit />
                </IconButton>
              </>
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Category
                </Typography>
                <Stack spacing={1}>
                  <Chip 
                    label={getCategoryName(companyData?.categoryId || 0)}
                    size="small"
                    sx={{ width: 'fit-content' }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Regions
                </Typography>
                <Stack spacing={1}>
                  {getRegionNames(companyData?.regions || []).map((region, index) => (
                    <Chip 
                      key={index} 
                      label={region} 
                      size="small"
                      sx={{ width: 'fit-content' }}
                    />
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Sectors
                </Typography>
                <Stack spacing={1}>
                  {getSectorNames(companyData?.sectors || []).map((sector, index) => (
                    <Chip 
                      key={index} 
                      label={sector} 
                      size="small"
                      sx={{ width: 'fit-content' }}
                    />
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Bio
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {companyData?.bio || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <LoadingSpinner loading={loading} />
      </Stack>
    </>
  );
}

export default CompanyServiceInfo; 